'use strict';

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import TimelineIcon from './TimelineIcon';
import ExpandHOC from '../../../components/ExpandHOC';
import { formatCustom, getTime } from '../../../utils/TimeUtils';
import Row from '../../../components/tag/Row';
import Span from '../../../components/tag/Span';
import {OTLink} from "../../../components/tag/OTLink";
import {
  isRemoteVisit,
  isPhoneVisit,
  getVideoConferenceLink,
} from '../../../utils/TimelineUtils';
import VideoConferenceLink from './VideoConferenceLink';

const defaultMaxHeight = '120px';

/**
 * Displays the date of the event
 * @param date date of the event
 * @param past whether event is upcoming or in the past
 * @param hideHours whether hours should be hidden from the display
 * @returns {*}
 * @constructor
 */
const DateDisplay = ({ index, date, past, hideHours }) => {
  let showDate = false;
  if (index === 'first' || index === 'only') {
    showDate = true;
  }

  let none = false;
  if (index === 'last' || index === 'middle') {
    none = true;
  }

  return (
      <div
          className={classnames(
              'date-container',
              {'date-container--past': past},
              {'date-container--none': none}
          )}
      >
        {showDate ? (
            <div className="day-container">
              <h2 className="day">
                {capitalize(date.format('ddd '))}
                <span>{date.format('D.M.')}</span>
              </h2>
              <div className="year">{date.format('YYYY')}</div>
            </div>
        ) : (
            <></>
        )}
      </div>
  );
};

/**
 * Displays the time of the event
 * @param date date of the event
 * @param past whether event is upcoming or in the past
 * @param hideHours whether hours should be hidden from the display
 * @returns {*}
 * @constructor
 */
const TimeDisplay = ({ index, date, past, hideHours }) => {
  return (
    <div
      className={classnames('time-container', { 'time-container--past': past })}
    >
      <div className="hour">
        {!hideHours && (
          <div>
            <span className="clock">
              <FormattedMessage id={'clock'}/>
            </span>
                {date.format('H.mm')}
              </div>
          )}
        </div>
      </div>
  );
};

/**
 * Displays the icon on the "line" of the timeline
 * @param past whether event is upcoming or in the past (affects color of the icon's background)
 * @param icon the icon's configuration
 * @param color the color of the background of the item
 * @returns {*}
 * @constructor
 */
const IconDisplay = ({ index, past, icon, color }) => {
  let none = false;
  if (index === 'last' || index === 'middle') {
    none = true;
  }

  return (
    <div className={classnames('icon-wrapper', { 'icon-wrapper--none': none })}>
      <TimelineIcon config={icon} color={color} past={past} />
    </div>
  );
};

/**
 * once clicked, the timeline item should reveal a link section.
 * state.expanded defines whether item expanded or not
 * @param toggleOpen function to attach to element where event can be toggled to expand
 * @param past whether event is upcoming or in the past
 * @param contentDescription description of the event (shows without expanding the event)
 * @param contentExpanded details of the event (shows after expanding the event)
 * @param setRef expandHoc callback
 * @param color color of the title of the card
 * @param title title of the card
 * @param style additional styles for the expanding part
 * @param index
 * @param date date of event
 * @param isOpen is set to open
 * @param isDisplayNone is display set to none
 * @param logo is set if needed
 * @return {*}
 **/
const CardDisplay = ({
                       id,
                       index,
                       toggleOpen,
                       date,
                       isOpen,
                       isDisplayNone,
                       past,
                       contentDescription,
                       contentExpanded,
                       setRef,
                       color,
                       title,
                       style,
                       logo,
                     }) => {
  let expandStyle = isDisplayNone ? {display: 'none'} : {};
  let isLogo = false;
  if (logo !== null && !!logo) {
    isLogo = true;
  }
  let only = false;
  if (index === 'last' || index === 'only') {
    only = true;
  }

  //let tabIndex = isDisplayNone ? -1: null;
  return (
      <div className={classnames(
          'card-wrapper',
          {'card-wrapper--past': past},
          {'card-wrapper--only': only}
      )}>
        <div className="card-container">
          <div className="card-arrow"/>
          <div className="card">
            <div className="card-normal">
                <Row>
                    <h2
                        className={classnames('card__event', {
                            past: past,
                            [color]: !past,
                        })}
                    >
                        <Span sm-len="12" len={isLogo ? '6' : '12'}>
                            {title && title.message && (
                                <FormattedMessage id={title.message} defaultMessage={title.defaultMessage}>
                                    {(txt) => <>{txt}</>}
                                </FormattedMessage>
                            )}
                            {title && !(title.message) && (
                                <span>{title}</span>
                            )}
                            {' '}
                            <span className="screen-reader-only">
                                {formatCustom(date, 'LLLL')}
                            </span>
                        </Span>
                        {isLogo && (
                            <Span len="6" sm-len="12">
                              <span
                                  className={classnames('card__logo', {
                                      past: past,
                                  })}
                              >
                                {logo}
                              </span>
                            </Span>
                        )}
                    </h2>
                </Row>

                <div className="card__description">
                    {contentDescription}
                </div>
            </div>

              {/*<div className="card-expanded" ref={setRef} style={style}>
              <div
                  className="card-expanded-wrapper"
                  aria-expanded={isOpen}
                  style={expandStyle}
              >
                {contentExpanded}
              </div>
            </div>*/}
            <div>
                <Row>
                    <Span className="card-frame-link-wrapper">
                        <OTLink className={'ot-button'} link={`/timeline/${id}`}>
                            <span>
                                <FormattedMessage id="show.preparation.info"/>
                            </span>
                        </OTLink>
                    </Span>
                </Row>
                {/*contentExpanded && (
                  <Row>
                <Span>
                  <button
                    className="card-expand-toggler-button"
                    onClick={toggleOpen}
                  >
                    {isOpen ? (
                      <span className="card-expand-toggler">
                        <i className="card-expand-toggler__icon icon-angle-up" />
                        <span className="card-expand-toggler__text">
                          <FormattedMessage id="hide.info" />
                        </span>
                      </span>
                    ) : (
                      <span className="card-expand-toggler expanded">
                        <i className="card-expand-toggler__icon icon-angle-down" />
                        <span className="card-expand-toggler__text">
                          <FormattedMessage id="show.more.info" />
                        </span>
                      </span>
                    )}
                  </button>
                </Span>
                  </Row>
              )*/}
            </div>
          </div>
        </div>
      </div>
  );
};

const TimelineItem = (props) => {
  const {
    data,
    date,
    logo,
    past,
    icon,
    color,
    title,
    contentDescription,
    contentExpanded,
    hideHours,
    toggleOpen,
    setRef,
    maxHeight,
    isOpen,
    isDisplayNone,
  } = props;
  const [cardTitle, setCardTitle] = useState(title);
  const [cardIcon, setCardIcon] = useState(icon);
  const [cardStyle, setCardStyle] = useState(null);

  const [momentDate] = useState(getTime(date));

  useEffect(() => {
    if (isPhoneVisit(data)) {
      setCardIcon({
        type: 'custom',
        name: 'phone',
      });
    }

    if (data.config && data.config.appointmentSpecificTitle) {
        setCardTitle(data.config.appointmentSpecificTitle);
    } else if (isRemoteVisit(data)) {
        setCardTitle({
            message: 'remote.timelineitem.title',
        });
    } else if (isPhoneVisit(data)) {
        setCardTitle({
            message: 'phone.timelineitem.title',
        });
    }

    if (isOpen) {
      setCardStyle({
        ...cardStyle,
        maxHeight: maxHeight || defaultMaxHeight,
        opacity: 1,
      });
    } else {
      setCardStyle({
        ...cardStyle,
        maxHeight: 0,
        opacity: 0,
      });
    }
  }, [props]);

  return (
      <Row className={classnames('ot-timeline-item', { expanded: isOpen })}>
        <IconDisplay
            index={data.index}
            past={past}
            icon={cardIcon}
            color={color}
        />
        <DateDisplay
            index={data.index}
            date={momentDate}
            past={past}
            hideHours={hideHours}
        />
        <TimeDisplay
            index={data.index}
            date={momentDate}
            past={past}
            hideHours={hideHours}
        />
        <CardDisplay
            id={data.id}
            index={data.index}
            date={momentDate}
            logo={logo}
            title={cardTitle}
            toggleOpen={toggleOpen}
            isOpen={isOpen}
            isDisplayNone={isDisplayNone}
            contentDescription={contentDescription}
            contentExpanded={contentExpanded}
            past={past}
            setRef={setRef}
            color={color}
            style={cardStyle}
        />
      </Row>
  );
};

TimelineItem.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.any,
    date: PropTypes.string,
    department: PropTypes.string,
    organization: PropTypes.string,
    videoConferenceUrl: PropTypes.string,
  }),
  past: PropTypes.bool,
};

export default ExpandHOC(TimelineItem);
